import React, {
  Children,
  MouseEvent,
  ReactElement,
  ReactNode,
  cloneElement,
  forwardRef,
  isValidElement,
  useState,
} from "react";

import {
  ListItemButton,
  ListItemButtonProps,
  Menu,
  MenuProps,
} from "@mui/material";

const NAME = "ListItemButtonMenu";

export type ListItemButtonMenuProps = ListItemButtonProps;

export const ListItemButtonMenu = forwardRef<
  HTMLDivElement,
  ListItemButtonMenuProps & {
    menu: ReactNode;
    menuProps?: Partial<MenuProps>;
  }
>(({ onClick, menu, menuProps, ...ListItemButtonProps }, ListItemButtonRef) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLDivElement>(null);

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    onClick?.(e);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <ListItemButton
        onClick={handleClick}
        ref={ListItemButtonRef}
        {...ListItemButtonProps}
      />
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        onClose={handleClose}
        open={open}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        {...menuProps}
      >
        {(() => {
          const processChildren = (children: ReactNode): ReactNode => {
            return Children.map(children, (child) => {
              if (!isValidElement(child)) return child;

              // Check if child is a Fragment
              if (
                child.type === React.Fragment ||
                child.type.toString() === "Symbol(react.fragment)"
              ) {
                // Recursively process fragment children
                return cloneElement(child as ReactElement, {
                  children: processChildren(child.props.children),
                });
              }

              // For regular elements, add onClick handler
              return cloneElement(child as ReactElement, {
                onClick: (e: MouseEvent<HTMLElement>) => {
                  handleClose();
                  if (child.props.onClick) child.props.onClick(e);
                },
              });
            });
          };

          return processChildren(menu);
        })()}
      </Menu>
    </>
  );
});
ListItemButtonMenu.displayName = NAME;
